import type { MouseEvent } from "react";
import { AlertTriangle, CalendarDays, EyeOff, Lock, RefreshCw } from "lucide-react";
import { DAY_END_MIN, DAY_START_MIN, formatClockRange } from "@/lib/calendar";
import type { Interval } from "@/lib/calendar-slots";
import { DarkPill } from "@/components/calendar/ui";
import { cn } from "@/lib/utils";

const PX_PER_MIN = 0.6; // 36px an hour
const HOURS = Array.from({ length: (DAY_END_MIN - DAY_START_MIN) / 60 + 1 }, (_, i) => DAY_START_MIN / 60 + i);
const y = (min: number) => (Math.max(DAY_START_MIN, Math.min(DAY_END_MIN, min)) - DAY_START_MIN) * PX_PER_MIN;
const hourLabel = (h: number) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? "AM" : "PM"}`;

export function TimelineLegend() {
  return (
    <div className="mt-4 flex items-center gap-4 text-[12.5px] text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[3px] bg-primary" /> This step
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[3px] bg-[#e6e1db]" /> Busy
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[3px] bg-[#e2efe4]" /> Free
      </span>
    </div>
  );
}

/**
 * "Your day": the step against busy blocks, 7 AM – 9 PM. Busy blocks carry no names —
 * Google only says when. Clicking a free space places the step there.
 */
export function DayTimeline({
  busy,
  loading,
  step,
  onPlace,
  compact = false,
  minMinutes,
}: {
  busy: Interval[];
  loading: boolean;
  step: { start: number; end: number; overlaps: boolean } | null;
  onPlace?: (minutes: number) => void;
  compact?: boolean;
  /** On today: time before this has passed — shaded, and tapping it does nothing. */
  minMinutes?: number;
}) {
  const height = (DAY_END_MIN - DAY_START_MIN) * PX_PER_MIN;
  const place = (e: MouseEvent<HTMLDivElement>) => {
    if (!onPlace || loading) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const min = Math.round((DAY_START_MIN + (e.clientY - rect.top) / PX_PER_MIN) / 15) * 15;
    if (minMinutes !== undefined && min < minMinutes) return;
    onPlace(min);
  };

  return (
    <div className={cn("relative flex", compact ? "mt-2" : "mt-4")}>
      <div className="relative w-12 shrink-0" style={{ height }}>
        {HOURS.map((h) => (
          <span key={h} className="absolute -translate-y-1/2 text-[10.5px] text-muted-foreground" style={{ top: y(h * 60) }}>
            {hourLabel(h)}
          </span>
        ))}
      </div>
      <div
        className={cn("relative flex-1", onPlace && !loading && "cursor-copy")}
        style={{ height }}
        onClick={place}
        role={onPlace ? "button" : undefined}
        aria-label={onPlace ? "Place the step at a time" : undefined}
      >
        {HOURS.map((h) => (
          <span key={h} aria-hidden className="absolute inset-x-0 h-px bg-foreground/[0.06]" style={{ top: y(h * 60) }} />
        ))}
        {!loading && <span aria-hidden className="absolute inset-x-0 inset-y-0 rounded-[10px] bg-[#e2efe4]/60" />}
        {!loading && minMinutes !== undefined && minMinutes > DAY_START_MIN && (
          <span
            aria-hidden
            className="absolute inset-x-0 top-0 z-[1] rounded-t-[10px] bg-[repeating-linear-gradient(135deg,rgba(47,41,37,0.05)_0_6px,transparent_6px_12px)] bg-[#f4f1ec]/80"
            style={{ height: y(minMinutes) }}
          />
        )}
        {busy.map((b) => {
          const h = Math.max(10, (Math.min(b.end, DAY_END_MIN) - Math.max(b.start, DAY_START_MIN)) * PX_PER_MIN);
          if (b.end <= DAY_START_MIN || b.start >= DAY_END_MIN) return null;
          return (
            <div
              key={`${b.start}-${b.end}`}
              className={cn(
                "absolute inset-x-0 flex overflow-hidden rounded-[8px] border border-foreground/[0.08] px-2.5 text-[11px] text-muted-foreground",
                loading ? "animate-pulse bg-foreground/[0.05]" : "bg-[#ebe7e2]",
                h <= 22 ? "items-center" : "items-start pt-1.5",
              )}
              style={{ top: y(b.start), height: h }}
            >
              {!loading && (
                <span className="flex items-center gap-1.5 truncate">
                  <Lock className="h-3 w-3 shrink-0" /> Busy · {formatClockRange(b.start, b.end)}
                </span>
              )}
            </div>
          );
        })}
        {step && !loading && (
          <div
            className={cn(
              "absolute inset-x-0 z-10 flex items-center overflow-hidden px-2.5 text-[11px] font-medium shadow-[0_4px_6px_rgba(110,83,172,0.25)]",
              step.overlaps ? "border-2 border-[#d9545e] bg-[#fdecec] text-[#a8323e]" : "bg-primary text-white",
              (step.end - step.start) * PX_PER_MIN <= 22 ? "rounded-full" : "rounded-[10px]",
            )}
            style={{ top: y(step.start), height: Math.max(14, (step.end - step.start) * PX_PER_MIN) }}
          >
            {step.overlaps && <AlertTriangle className="mr-1 h-3 w-3 shrink-0" />}
            <span className="truncate">
              {formatClockRange(step.start, step.end)} · {step.overlaps ? "overlaps" : "This step"}
            </span>
          </div>
        )}
        {loading && (
          <div className="absolute inset-x-4 top-1/2 z-10 flex -translate-y-1/2 justify-center">
            <span className="flex items-center gap-2 rounded-full border border-foreground/[0.08] bg-white px-4 py-2 text-[13px] text-foreground shadow-[0_4px_16px_-6px_rgba(47,41,37,0.2)]">
              <RefreshCw className="h-3.5 w-3.5 animate-spin text-primary [animation-duration:2s]" />
              Checking when you&apos;re busy · Google Calendar
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function FromGoogleBadge() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-foreground/[0.05] px-2.5 py-1 text-[11.5px] text-muted-foreground">
      <CalendarDays className="h-3 w-3" /> From Google Calendar
    </span>
  );
}

/**
 * J11 · without free/busy: the right column explains what it would show and offers
 * access once — or, if the Settings switch is what's off, turning it back on.
 */
export function FreeBusyEmptyState({
  reason,
  pending,
  onAllow,
  onNotNow,
}: {
  reason: "not_allowed" | "switched_off" | "ended" | "failed";
  pending: boolean;
  onAllow: () => void;
  onNotNow: () => void;
}) {
  return (
    <div className="mt-4 flex flex-1 flex-col items-center justify-center rounded-[18px] border border-foreground/[0.07] px-6 py-10 text-center">
      <div aria-hidden className="mb-6 w-full max-w-[300px] space-y-[5px] opacity-50">
        {["7 AM", "8 AM", "9 AM", "10 AM", "11 AM", "12 PM", "1 PM", "2 PM", "3 PM"].map((t) => (
          <div key={t} className="flex items-center gap-3">
            <span className="w-9 text-right text-[9px] text-muted-foreground">{t}</span>
            <span className="h-px flex-1 bg-foreground/10" />
          </div>
        ))}
      </div>
      <p className="text-[19px] font-semibold text-foreground">
        {reason === "ended" ? "Your calendar connection ended" : reason === "failed" ? "Couldn't check your calendar" : "See your free time here"}
      </p>
      <p className="mt-2 max-w-[330px] text-[14px] leading-relaxed text-muted-foreground">
        {reason === "failed"
          ? "Google Calendar didn't answer just now, so these are common times rather than free ones. You can still pick any time."
          : reason === "ended"
          ? "Google ended UnfoldWIN's access. Reconnect to see your free time and add steps. Events already added are still in your calendar."
          : "Let UnfoldWIN check when you're busy, so it can suggest times that fit. It sees busy and free times only, never what your events are."}
      </p>
      <DarkPill className="mt-5" loading={pending} onClick={onAllow}>
        {!pending && (reason === "ended" || reason === "failed" ? <RefreshCw className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />)}
        {reason === "failed"
          ? "Try again"
          : reason === "ended"
            ? "Reconnect"
            : reason === "not_allowed"
              ? "Allow free/busy access"
              : "Turn on free-time suggestions"}
      </DarkPill>
      <button type="button" onClick={onNotNow} className="mt-3 text-[13.5px] font-medium text-primary underline underline-offset-4">
        Not now, I&apos;ll pick a time myself
      </button>
      <p className="mt-3 text-xs text-muted-foreground">
        {reason === "failed"
          ? "Nothing on your calendar changed."
          : reason === "not_allowed"
          ? "Google will ask you for one more permission."
          : reason === "ended"
            ? "Google opens in this tab, then brings you back."
            : "You switched this off in Settings."}
      </p>
    </div>
  );
}
